import {
  Permissions,
  TFeedSpaceItem,
  TSpaceBase,
  TUserAccessMap,
  canPerformSpaceAction,
  hasPermissionForApp,
} from '@onesource/schemas';

/**
 * Resolve a space's effective app. Feed/sub-spaces don't store `app` directly —
 * they inherit it from their parent container (same rule as the backend
 * `SpacesService.getApp`), so fall back to `parent.app`.
 */
export const resolveSpaceApp = (space: Pick<TSpaceBase, 'app' | 'parent'> | null | undefined): string | null =>
  space?.app ?? space?.parent?.app ?? null;

/**
 * Whether the user can manage (admin) the given space — i.e. their access entry
 * for that space's app grants `space.manage`. Mirrors the backend
 * `SpacePermissionGuard`. Use it to gate admin-only UI (admin post types,
 * pin/schedule controls) in the community hub.
 */
export const userCanManageSpace = (
  access: TUserAccessMap | undefined | null,
  space: Pick<TSpaceBase, 'app' | 'parent'> | null | undefined,
): boolean => hasPermissionForApp(access ?? undefined, resolveSpaceApp(space), Permissions.SPACE_MANAGE);

/**
 * Whether the user may perform an action (e.g. `post.create`) in a space. Their
 * role for the app must grant it AND they must pass any per-space override an
 * admin set on the sub-space or its container — the mirror of
 * `SpacePermissionGuard` steps 4 and 4b.
 *
 * Use it to hide what the API would reject: in a Q&A community `post.create` is
 * restricted to teachers, so members see the feed and the comment box but no
 * composer.
 */
export const userCanActInSpace = (
  access: TUserAccessMap | undefined | null,
  permission: string,
  space: Pick<TSpaceBase, 'app' | 'parent' | 'actionPermissionIds'> | null | undefined,
  parentSpace?: Pick<TSpaceBase, 'app' | 'parent' | 'actionPermissionIds'> | null,
): boolean =>
  canPerformSpaceAction(
    permission,
    access ?? undefined,
    resolveSpaceApp(space) ?? resolveSpaceApp(parentSpace),
    space,
    parentSpace,
  );

// `appId` is a plain string (not the TAppNameEnum union) so callers can pass a
// backend app key; the access map is keyed by the app string.
export const hasAppAccess = (access: TUserAccessMap | undefined | null, appId: string): boolean => {
  const permissions = access?.[appId]?.permissions ?? [];
  return permissions.some(p => p.name === Permissions.APP_ACTIVE);
};

/**
 * Whether the user has active access (`sub.active`) for a given app key.
 * Used to gate community-hub spaces, which are keyed by their `app` string.
 */
export const hasSpaceAccess = (access: TUserAccessMap | undefined | null, app: string | null | undefined): boolean => {
  if (!app) {
    return false;
  }
  const permissions = access?.[app]?.permissions ?? [];
  return permissions.some(p => p.name === Permissions.APP_ACTIVE);
};

/**
 * Whether the user can enter the Community hub at all — i.e. they have at least
 * one community/feed space they can access. Drives both the sidebar Community
 * menu visibility and the community route guard.
 */
export const hasCommunityAccess = (feedSpaces: TFeedSpaceItem[] | undefined | null): boolean =>
  Array.isArray(feedSpaces) && feedSpaces.some(space => space.hasAccess);
